/**
 * Profile Results Banner Component - Ryder Crypto Wallet
 * 
 * Displays personalized quiz results at the top of product pages
 * when user arrives via quiz funnel with profile parameter
 * 
 * Features:
 * - Multiple badges based on quiz tags (beginner, security-focused, etc.)
 * - Two-step reveal animation: banner appears collapsed, page fades in, then expands
 * - Reassurance section with value propositions
 * - No dismiss button - section always stays visible
 * - Mobile-optimized CTA layout
 */

import { useState, useEffect } from 'react';
import { Shield, TrendingUp, CheckCircle2, Wallet, ChevronDown, Lock, Smartphone, AlertCircle, Info, Star, Zap } from 'lucide-react';
import { usePricing } from '@/contexts/PricingContext';

interface ProfileData {
  id: string;
  name: string;
  description: string;
  overallScore: number; // 0-100 score
  insights: string[];
  stats: { label: string; value: string; positive?: boolean; score?: number }[]; // score 0-100 for progress bar
}

// Map quiz slugs to profile IDs
const slugToProfileId: Record<string, string> = {
  'crypto-beginner': 'beginner',
  'growing-investor': 'growing-investor',
  'defi-user': 'defi-user',
  'hodler': 'hodler',
};

// Badge definitions based on quiz tags
interface BadgeConfig {
  id: string;
  label: string;
  icon: React.ReactNode;
  tags: string[]; // Tags that trigger this badge
  color: string;
}

const badgeConfigs: BadgeConfig[] = [
  { 
    id: 'beginner', 
    label: 'Perfect for Beginners', 
    icon: <Star className="w-3 h-3" />, 
    tags: ['beginner', 'easy-to-use'],
    color: 'bg-blue-100 text-blue-700'
  },
  { 
    id: 'intermediate', 
    label: 'Growing Portfolio', 
    icon: <TrendingUp className="w-3 h-3" />, 
    tags: ['intermediate', 'growing'],
    color: 'bg-green-100 text-green-700'
  },
  { 
    id: 'experienced', 
    label: 'Power User', 
    icon: <Zap className="w-3 h-3" />, 
    tags: ['experienced', 'defi'],
    color: 'bg-purple-100 text-purple-700'
  },
  { 
    id: 'security', 
    label: 'Security-Focused', 
    icon: <Shield className="w-3 h-3" />, 
    tags: ['security-focused', 'recovery'],
    color: 'bg-red-100 text-red-700'
  },
  { 
    id: 'self-custody', 
    label: 'Self-Custody Advocate', 
    icon: <Lock className="w-3 h-3" />, 
    tags: ['self-custody', 'exchange-user'],
    color: 'bg-orange-100 text-orange-700'
  },
  { 
    id: 'multi-chain', 
    label: 'Multi-Chain User', 
    icon: <Wallet className="w-3 h-3" />, 
    tags: ['multi-chain', 'ethereum', 'bitcoin'],
    color: 'bg-indigo-100 text-indigo-700'
  },
  { 
    id: 'mobile', 
    label: 'Mobile-First', 
    icon: <Smartphone className="w-3 h-3" />, 
    tags: ['mobile-first', 'convenience'],
    color: 'bg-teal-100 text-teal-700'
  },
];

// Reassurance messages based on quiz tags
interface ReassuranceMessage {
  tags: string[];
  title: string;
  message: string;
}

const reassuranceMessages: ReassuranceMessage[] = [
  {
    tags: ['beginner', 'easy-to-use'],
    title: 'Designed for Beginners',
    message: 'Ryder One makes self-custody simple. No technical knowledge required. Setup takes 5 minutes, and our interface is as easy to use as your banking app.'
  },
  {
    tags: ['security-focused', 'recovery'],
    title: 'Bank-Level Security',
    message: 'Your private keys are stored in a secure element chip (same tech used in passports). The included Recovery Tag ensures you\'ll never lose access to your funds.'
  },
  {
    tags: ['self-custody', 'exchange-user'],
    title: 'Take Control of Your Assets',
    message: 'After FTX, Celsius, and countless exchange hacks, self-custody isn\'t optional anymore. Ryder makes it easy to be your own bank without the complexity.'
  },
  {
    tags: ['multi-chain', 'ethereum', 'bitcoin'],
    title: 'One Device, All Your Chains',
    message: 'Manage Bitcoin, Ethereum, Stacks, and Solana from a single device, plus top ERC-20 and SPL tokens. No more juggling multiple wallets.'
  },
  {
    tags: ['defi', 'experienced'],
    title: 'Built for Advanced Users',
    message: 'Advanced features like multi-sig support and hardware-accelerated transaction signing. Everything you need for serious crypto management.'
  },
  {
    tags: ['hodl', 'high-value'],
    title: 'Secure Long-Term Storage',
    message: 'Whether you\'re holding $1K or $1M, Ryder One provides institutional-grade security. Air-gapped signing, tamper-proof hardware, and EAL6+ certified secure element.'
  },
];

const profileConfigs: Record<string, ProfileData> = {
  'beginner': {
    id: 'beginner',
    name: 'Crypto Beginner',
    description: 'You\'re new to crypto, and Ryder One is the perfect way to start your self-custody journey.',
    overallScore: 85,
    insights: [
      'Self-custody is simpler than you think - setup takes just 5 minutes',
      'You don\'t need to be technical to protect your crypto',
      'Starting with good habits (self-custody) prevents costly mistakes later',
    ],
    stats: [
      { label: 'Ease of Use', value: 'Beginner-Friendly', positive: true, score: 90 },
      { label: 'Security Level', value: 'Bank-Grade', positive: true, score: 95 },
      { label: 'Setup Time', value: '5 Minutes', positive: true, score: 85 },
    ],
  },
  'growing-investor': {
    id: 'growing-investor',
    name: 'Growing Investor',
    description: 'Your portfolio is growing, and it\'s time to upgrade to professional-grade security.',
    overallScore: 88,
    insights: [
      'Once your portfolio hits $1K+, self-custody becomes essential',
      'Ryder One scales with you - from beginner to power user',
      'Multi-chain support means you\'re ready for any opportunity',
    ],
    stats: [
      { label: 'Portfolio Protection', value: 'Institutional-Grade', positive: true, score: 92 },
      { label: 'Chain Support', value: 'BTC, ETH, SOL, STX + tokens', positive: true, score: 88 },
      { label: 'Future-Proof', value: 'Firmware Updates', positive: true, score: 85 },
    ],
  },
  'defi-user': {
    id: 'defi-user',
    name: 'DeFi Power User',
    description: 'You\'re active in DeFi and need multi-chain support. Ryder One handles it all.',
    overallScore: 92,
    insights: [
      'Hardware-accelerated signing for secure transactions',
      'Multi-sig support for advanced security setups',
      'Secure firmware with EAL6+ certified chip',
    ],
    stats: [
      { label: 'DeFi Compatibility', value: 'Full Support', positive: true, score: 95 },
      { label: 'Transaction Speed', value: 'Hardware-Accelerated', positive: true, score: 90 },
      { label: 'Advanced Features', value: 'Multi-Sig Ready', positive: true, score: 92 },
    ],
  },
  'hodler': {
    id: 'hodler',
    name: 'Security-First HODLer',
    description: 'You\'re holding for the long term and security is everything. Ryder One is built for you.',
    overallScore: 95,
    insights: [
      'Air-gapped signing keeps your keys offline and secure',
      'Recovery Tag ensures you\'ll never lose access to your funds',
      'Tamper-proof hardware detects any physical attacks',
    ],
    stats: [
      { label: 'Security Rating', value: 'Maximum', positive: true, score: 98 },
      { label: 'Recovery Options', value: 'Redundant Backup', positive: true, score: 95 },
      { label: 'Long-Term Storage', value: 'Optimal', positive: true, score: 92 },
    ],
  },
};

interface ProfileResultsBannerProps {
  profileId: string;
  email?: string;
  tags?: string[];
  labels?: string[];
  onRestartQuiz: () => void;
  onOpenCheckout?: () => void;
  pageId: string;
}

export default function ProfileResultsBanner({ 
  profileId, 
  email, 
  tags = [], 
  labels = [],
  onRestartQuiz,
  onOpenCheckout,
  pageId 
}: ProfileResultsBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const pricing = usePricing();

  // Map slug to profile ID if needed
  const mappedProfileId = slugToProfileId[profileId] || profileId;
  const profile = profileConfigs[mappedProfileId] || profileConfigs['beginner'];

  // Get badges based on tags
  const activeBadges = badgeConfigs.filter(badge => 
    badge.tags.some(tag => tags.includes(tag))
  ).slice(0, 4); // Max 4 badges

  // Get reassurance message based on tags
  const reassurance = reassuranceMessages.find(msg => 
    msg.tags.some(tag => tags.includes(tag))
  ) || reassuranceMessages[0];

  // Trigger expansion animation after mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExpanded(true);
      setHasAnimated(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="w-full bg-gradient-to-br from-purple-50 to-blue-50 border-b-2 border-purple-200" data-profile-banner>
      <div className="container mx-auto px-4 py-6">
        {/* Collapsed Header - Always Visible */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-bold text-gray-900">
                Your Quiz Results: {profile.name}
              </h3>
            </div>
            
            {/* Badges */}
            {activeBadges.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {activeBadges.map(badge => (
                  <span 
                    key={badge.id}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}
                  >
                    {badge.icon}
                    {badge.label}
                  </span>
                ))}
              </div>
            )}
            
            <p className="text-sm text-gray-700">{profile.description}</p>
          </div>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-sm font-medium text-purple-700 hover:text-purple-900 transition-colors whitespace-nowrap"
          >
            {isExpanded ? 'Hide Details' : 'View Details'}
            <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Expanded Content */}
        <div 
          className={`overflow-hidden transition-all duration-500 ${
            isExpanded ? 'max-h-[1000px] opacity-100 mt-6' : 'max-h-0 opacity-0'
          }`}
        >
          {/* Reassurance Section */}
          <div className="bg-white rounded-lg p-6 mb-6 border-2 border-purple-200">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-purple-700" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">{reassurance.title}</h4>
                <p className="text-sm text-gray-700">{reassurance.message}</p>
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-lg p-5 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-purple-600" />
                Why Ryder One is Perfect for You
              </h4>
              <ul className="space-y-2">
                {profile.insights.map((insight, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{insight}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-lg p-5 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-purple-600" />
                Your Match Score
              </h4>
              <div className="space-y-3">
                {profile.stats.map((stat, idx) => (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{stat.label}</span>
                      <span className={`text-sm font-bold ${stat.positive ? 'text-green-600' : 'text-gray-600'}`}>
                        {stat.value}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-1000 ${
                          stat.positive ? 'bg-green-500' : 'bg-gray-400'
                        }`}
                        style={{ width: `${stat.score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-6 text-white">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-lg mb-1">Ready to Take Control?</h4>
                <p className="text-sm text-purple-100">
                  Join 2,500+ users who've secured their crypto with Ryder One
                </p>
              </div>
              <button
                onClick={() => {
                  if (onOpenCheckout) {
                    onOpenCheckout();
                  } else {
                    const element = document.getElementById('bundle-section');
                    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
                className="px-6 py-3 bg-white text-purple-700 rounded-lg font-bold hover:bg-purple-50 transition-colors whitespace-nowrap"
              >
                {`Get Ryder One - $${pricing.salePrice}`}
              </button>
            </div>
          </div>

          {/* Retake Quiz Link */}
          <div className="mt-4 text-center">
            <button
              onClick={onRestartQuiz}
              className="text-sm text-purple-700 hover:text-purple-900 underline"
            >
              Retake the quiz
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
